import { createApp as createVueApp } from 'vue';
import { createPinia } from 'pinia';
import { createAppRouter } from './router.js';
import App from './App.vue';
import { initApi } from './api/wordpress.js';
import { useFormStore } from './stores/form.js';

/**
 * Create and mount Vue app
 *
 * @param {HTMLElement} container DOM element to mount app to
 */
export function createApp( container ) {
	const pinia = createPinia();
	const app = createVueApp( App );

	app.use( pinia );
	
	// Create router lazily - only when app is being initialized
	const router = createAppRouter();
	app.use( router );

	// Get initial data from container data attributes
	const carId = container.dataset.carId;
	const vehicleUlid = container.dataset.vehicleUlid || '';
	const restUrl = container.dataset.restUrl || '/wp-json/greven/v1';
	const copyConfigRaw = container.dataset.copyConfig || '{}';
	const vehicleTypeCode = container.dataset.vehicleTypeCode || '';
	const vehicleDataRaw = container.dataset.vehicle || '';

	/**
	 * Copy configuration coming from PHP/ACF.
	 *
	 * The PHP template (`car-single.php`) exposes a JSON-encoded object via
	 * the data-copy-config attribute on #car-form-app. New configurable
	 * strings/USPs for the form can be added by:
	 * - Creating a new ACF option field.
	 * - Adding it to the $copy_config array in the PHP template.
	 * - Reading the value here from copyConfig[key] in your Vue components.
	 */
	let copyConfig = {};
	try {
		copyConfig = JSON.parse( copyConfigRaw ) || {};
	} catch ( e ) {
		copyConfig = {};
	}

	// Initialize API client
	initApi( restUrl );

	// Initialize form store with car ID or aanbod-new ULID (must be called after app.use(pinia))
	const formStore = useFormStore();
	if ( vehicleUlid ) {
		formStore.setVehicleUlid( vehicleUlid );
	} else if ( carId ) {
		formStore.setCarId( parseInt( carId, 10 ) );
	}

	// Seed the inlined vehicle payload (data-vehicle) so fetchCarData() can skip
	// the extra REST round-trip for data the page already rendered server-side.
	if ( vehicleDataRaw ) {
		try {
			const vehicleData = JSON.parse( vehicleDataRaw );
			if ( vehicleData && typeof vehicleData === 'object' ) {
				formStore.setInitialVehicleData( vehicleData );
			}
		} catch ( e ) {
			// Malformed payload: fall back to the REST fetch in fetchCarData().
		}
	}

	app.provide( 'carId', carId );
	app.provide( 'vehicleUlid', vehicleUlid );
	app.provide( 'restUrl', restUrl );
	app.provide( 'copyConfig', copyConfig );
	app.provide( 'vehicleTypeCode', vehicleTypeCode );

	app.mount( container );
}

